/**
 * JARVIS phone camera — application wiring.
 *
 * Two screens: pair, then use. The camera track and push-to-talk audio go
 * peer-to-peer to the Windows app; the Cloudflare Worker only carried the SDP
 * and ICE that established the link.
 *
 * Nothing in this UI displays a value the browser or the peer did not report.
 * Unknown readings stay "non mesuré".
 */

import { Camera, DEFAULT_SETTINGS, type CameraSettings } from './camera.ts';
import { awaitApproval, claim, PairingError } from './pairing.ts';
import { Peer, type TransportInfo } from './peer.ts';
import { Signaling, type LinkState } from './signaling.ts';
import {
  clearCredential,
  devicePublicId,
  guessDeviceName,
  loadCredential,
  loadEndpoint,
  saveCredential,
  saveEndpoint,
  takePairingFromUrl,
} from './storage.ts';
import { Talk } from './talk.ts';
import type { DataChannelMessage, DeviceCredential, PeerInfo } from '../../shared/signaling/protocol.ts';

/* ------------------------------------------------------------------ dom --- */

const $ = <T extends HTMLElement>(id: string): T => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`missing element #${id}`);
  return el as T;
};

const screenPair = $('screen-pair');
const screenApp = $('screen-app');

const pairLink = $<HTMLInputElement>('pair-link');
const pairEndpoint = $<HTMLInputElement>('pair-endpoint');
const pairGo = $<HTMLButtonElement>('pair-go');
const pairState = $('pair-state');
const pairLede = $('pair-lede');

const barDot = $('bar-dot');
const barState = $('bar-state');
const barRtt = $('bar-rtt');
const barMenu = $<HTMLButtonElement>('bar-menu');

const view = $<HTMLVideoElement>('view');
const viewEmpty = $('view-empty');
const viewHint = $('view-hint');
const tagRes = $('tag-res');
const tagFacing = $('tag-facing');

const actCamera = $<HTMLButtonElement>('act-camera');
const actFlip = $<HTMLButtonElement>('act-flip');
const actTorch = $<HTMLButtonElement>('act-torch');
const actTalk = $<HTMLButtonElement>('act-talk');
const actTalkLabel = $('act-talk-label');
const talkState = $('talk-state');

const sayYou = $('say-you-text');
const sayPc = $('say-pc-text');
const captions = $('captions');

const fState = $('f-state');
const fPath = $('f-path');
const fAddr = $('f-addr');
const fRtt = $('f-rtt');

const sheet = $('sheet');
const scrim = $<HTMLButtonElement>('scrim');
const cellZoom = $('cell-zoom');
const setZoom = $<HTMLInputElement>('set-zoom');
const setDevice = $('set-device');
const actUnpair = $<HTMLButtonElement>('act-unpair');

/* ---------------------------------------------------------------- state --- */

const camera = new Camera();
const talk = new Talk();
let signaling: Signaling | null = null;
let peer: Peer | null = null;
let credential: DeviceCredential | null = null;
let pcOnline = false;
let settings: CameraSettings = { ...DEFAULT_SETTINGS };
let torchOn = false;

/* ================================================================ PAIR === */

pairEndpoint.value = loadEndpoint();

/**
 * A pairing link may arrive three ways: this app opened from the QR, the user
 * pasted the whole URL, or the user pasted just the fragment. All three reduce
 * to an install id plus a token.
 */
function parsePairInput(raw: string): { installId: string; token: string } | null {
  const text = raw.trim();
  if (!text) return null;
  const hash = text.includes('#') ? text.slice(text.indexOf('#') + 1) : text;
  const p = new URLSearchParams(hash);
  const installId = p.get('i');
  const token = p.get('t');
  return installId && token ? { installId, token } : null;
}

let pairAbort: AbortController | null = null;

async function runPairing(installId: string, token: string): Promise<void> {
  const endpoint = pairEndpoint.value.trim().replace(/\/+$/, '');
  if (!endpoint) {
    setPairState('Indiquez l’adresse du serveur de signalisation.', 'bad');
    return;
  }
  saveEndpoint(endpoint);

  pairAbort?.abort();
  pairAbort = new AbortController();
  pairGo.disabled = true;

  try {
    setPairState('Demande envoyée…', 'warn');
    const opened = await claim(endpoint, installId, token, guessDeviceName(), devicePublicId());

    setPairState('Autorisez cet appareil sur le PC…', 'warn');
    const cred = await awaitApproval(endpoint, installId, opened.claimId, pairAbort.signal, (s) => {
      setPairState(`Autorisez cet appareil sur le PC… ${s} s`, 'warn');
    });

    saveCredential(cred);
    credential = cred;
    setPairState('Appairé.', 'ok');
    enterApp();
  } catch (err) {
    /* A raw TypeError from fetch reads "Failed to fetch" — English network
       jargon on a French screen. Name the actual problem and the fix. */
    let message = err instanceof PairingError || err instanceof Error ? err.message : 'Échec inconnu.';
    if (err instanceof TypeError) {
      message = 'Serveur de signalisation injoignable — vérifiez l’adresse ci-dessous.';
    }
    /* A consumed or expired token is a dead link: clear it so the retry cannot
       loop on the same QR, and say what to do instead. */
    if (err instanceof PairingError && err.code === '401') {
      pairLink.value = '';
      pairLede.textContent = 'Générez un nouveau code sur le PC, puis scannez-le à nouveau.';
    }
    setPairState(message, 'bad');
  } finally {
    pairGo.disabled = false;
  }
}

pairGo.addEventListener('click', () => {
  const parsed = parsePairInput(pairLink.value);
  if (!parsed) {
    setPairState("Ce lien ne contient pas d'information d'appairage.", 'bad');
    return;
  }
  void runPairing(parsed.installId, parsed.token);
});

function setPairState(text: string, tone: 'idle' | 'ok' | 'warn' | 'bad'): void {
  pairState.textContent = text;
  pairState.dataset.tone = tone;
}

/* ================================================================= APP === */

function enterApp(): void {
  if (!credential) return;
  screenPair.hidden = true;
  screenApp.hidden = false;
  setDevice.textContent = `${credential.deviceName} · ${credential.deviceId.slice(0, 8)}`;
  connectSignaling();
}

function connectSignaling(): void {
  if (!credential) return;
  signaling?.stop();

  signaling = new Signaling(loadEndpoint(), credential, {
    state: onLinkState,
    hello: () => undefined,
    presence: onPresence,
    offer: () => undefined,
    answer: (sessionId, sdp) => void peer?.acceptAnswer(sessionId, sdp),
    ice: (sessionId, candidate) => void peer?.addIce(sessionId, candidate),
    bye: () => teardownPeer('Le PC a fermé la session.'),
    error: (code, message) => {
      /* peer-offline is expected whenever the PC is not running; it is not a
         fault worth shouting about. */
      if (code !== 'peer-offline') setBar(message, 'warn');
    },
    rtt: (ms) => {
      barRtt.textContent = `${ms} ms`;
    },
    cameraCommand: (sessionId, command, value) => void handleCameraCommand(sessionId, command, value),
  });
  signaling.connect();
}

async function handleCameraCommand(sessionId: string, command: string, value: unknown): Promise<void> {
  // Ignore commands for a different session — late ICE after a restart can carry stale ids.
  if (peer && sessionId !== peer.session) return;
  // Reuse the existing local code paths — never duplicate torch/zoom/lens logic.
  if (command === 'switch-lens') {
    const target = value === 'front' ? 'user' : value === 'back' ? 'environment' : null;
    if (!target || target === settings.facing) return;
    settings = { ...settings, facing: target as typeof settings.facing };
    try {
      await camera.start(settings);
      view.srcObject = camera.mediaStream;
      refreshCameraReadout();
      await publishCameraTrack();
      publishCapabilities();
    } catch {
      // Revert so the PC's next command sees the true state
      settings = { ...settings, facing: target === 'user' ? 'environment' : 'user' };
      refreshCameraReadout();
    }
    return;
  }
  if (command === 'zoom') {
    const n = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(n)) return;
    // Reuse camera.setZoom — same path as the local slider
    await camera.setZoom(n).catch(() => {});
    return;
  }
  if (command === 'torch') {
    const want = value === true || value === 'true';
    // Reuse camera.setTorch — same path as the local torch button
    const applied = await camera.setTorch(want).catch(() => false);
    if (applied) {
      torchOn = want;
      actTorch.setAttribute('aria-pressed', String(torchOn));
    }
    return;
  }
}

function publishCapabilities(): void {
  // Sends real capabilities from getCapabilities() — never a guess.
  const report = camera.getCapabilitiesReport();
  if (!report || !signaling?.online) return;
  const sessionId = peer?.session ?? 'init';
  signaling.sendCameraCapabilities(sessionId, report.torch, report.zoom, report.lenses);
}

function onLinkState(state: LinkState, detail?: string): void {
  switch (state) {
    case 'online':
      setBar('en ligne', 'ok');
      break;
    case 'connecting':
      setBar('connexion…', 'warn');
      barRtt.textContent = '';
      break;
    case 'retrying':
      setBar(detail ?? 'reconnexion…', 'warn');
      /* A round-trip from the old socket says nothing about the new one. */
      barRtt.textContent = '';
      break;
    case 'revoked':
      setBar('révoqué', 'bad');
      /* A revoked credential is dead: send the user back to pairing rather than
         letting the app retry a link that can never open. */
      forgetPairing(detail ?? 'Appareil révoqué depuis le PC.');
      break;
    case 'rejected':
      setBar('refusé', 'bad');
      forgetPairing(detail ?? "Le PC n'a pas accepté cet appareil.");
      break;
    default:
      setBar('hors ligne', 'off');
      barRtt.textContent = '';
      break;
  }
}

function onPresence(peers: PeerInfo[]): void {
  const wasOnline = pcOnline;
  pcOnline = peers.some((p) => p.role === 'pc');

  if (!pcOnline) {
    talkState.textContent = 'Le PC est hors ligne.';
    talkState.dataset.tone = 'warn';
    actTalk.disabled = true;
    if (wasOnline) teardownPeer('Le PC est hors ligne.');
    return;
  }

  talkState.textContent = camera.isActive ? 'Prêt.' : 'Démarrez la caméra pour parler.';
  talkState.dataset.tone = 'idle';
  actTalk.disabled = !peer?.connected;

  /* The PC just appeared while our camera is already live: offer immediately
     rather than waiting for the user to press anything again. */
  if (!wasOnline && camera.isActive && !peer) void startPeer();
}

function setBar(text: string, tone: 'ok' | 'warn' | 'bad' | 'off'): void {
  barState.textContent = text;
  barDot.dataset.tone = tone;
}

/* -------------------------------------------------------------- camera --- */

actCamera.addEventListener('click', () => void toggleCamera());

async function toggleCamera(): Promise<void> {
  if (camera.isActive) {
    stopCamera();
    return;
  }
  try {
    await camera.start(settings);
    view.srcObject = camera.mediaStream;
    view.hidden = false;
    viewEmpty.hidden = true;
    actCamera.textContent = 'Arrêter la caméra';

    actFlip.disabled = !(await Camera.hasMultipleCameras());
    refreshCameraReadout();
    // Capabilities must be sent right after the stream starts, from real getCapabilities()
    publishCapabilities();
    if (pcOnline) await startPeer();
    else {
      // Even without a peer yet, the PC will receive capabilities once it connects
      // because the signaling socket will deliver on next presence. Also publish after peer starts.
    }
    // If a peer now exists, publish again with the real sessionId
    if (peer) publishCapabilities();
  } catch (err) {
    /* Permission denial and absent hardware are different problems and get
       different words. The status bar is not touched: it reports the signaling
       link, and a camera fault there would outlive the fault itself. */
    const name = err instanceof DOMException ? err.name : '';
    viewHint.textContent =
      name === 'NotAllowedError'
        ? 'Accès caméra refusé. Autorisez-le dans les réglages du navigateur, puis réessayez.'
        : name === 'NotFoundError'
          ? 'Aucune caméra détectée sur cet appareil.'
          : err instanceof Error
            ? err.message
            : 'La caméra ne démarre pas.';
    viewEmpty.hidden = false;
  }
}

function stopCamera(): void {
  teardownPeer();
  camera.stop();
  view.srcObject = null;
  view.hidden = true;
  viewEmpty.hidden = false;
  viewHint.textContent = 'Touchez « Démarrer » pour autoriser la caméra.';
  actCamera.textContent = 'Démarrer la caméra';
  actFlip.disabled = true;
  actTorch.disabled = true;
  actTalk.disabled = true;
  tagRes.textContent = '—';
  tagFacing.textContent = '—';
  signaling?.sendCameraState(camera.readout());
}

actFlip.addEventListener('click', () => void flip());

async function flip(): Promise<void> {
  const next = settings.facing === 'environment' ? 'user' : 'environment';
  settings = { ...settings, facing: next };
  try {
    await camera.start(settings);
    view.srcObject = camera.mediaStream;
    refreshCameraReadout();
    publishCapabilities();
    /* Replacing the track keeps the same peer connection, so the PC sees no
       interruption and no renegotiation is needed. */
    await publishCameraTrack();
  } catch {
    /* Revert the intent so the label keeps matching what is actually running. */
    settings = { ...settings, facing: next === 'user' ? 'environment' : 'user' };
    refreshCameraReadout();
  }
}

actTorch.addEventListener('click', () => {
  const next = !torchOn;
  /* The key only prints the state the device confirmed: pressed flips after
     the constraint lands, and a refusal is named, never swallowed. */
  void camera
    .setTorch(next)
    .then((applied) => {
      if (!applied) {
        talkState.textContent = 'Torche non prise en charge par cette caméra.';
        talkState.dataset.tone = 'warn';
        return;
      }
      torchOn = next;
      actTorch.setAttribute('aria-pressed', String(torchOn));
    })
    .catch(() => {
      torchOn = false;
      actTorch.setAttribute('aria-pressed', 'false');
      talkState.textContent = 'La torche n’a pas changé d’état.';
      talkState.dataset.tone = 'bad';
    });
});

function refreshCameraReadout(): void {
  const r = camera.readout();

  tagRes.textContent =
    r.width && r.height
      ? `${r.width}×${r.height}${r.frameRate ? ` · ${r.frameRate}i/s` : ''}`
      : 'non mesuré';
  /* `facingMode` is absent on desktop browsers and on some Android builds. Fall
     back to the setting we asked for, marked as requested rather than measured,
     instead of printing "non mesuré" next to a working camera. */
  tagFacing.textContent =
    r.facing === 'user'
      ? 'avant'
      : r.facing === 'environment'
        ? 'arrière'
        : r.active
          ? `${settings.facing === 'user' ? 'avant' : 'arrière'} (demandé)`
          : 'non mesuré';

  /* Only expose controls the device actually reported. */
  actTorch.disabled = !r.supports.torch;
  if (!r.supports.torch) {
    torchOn = false;
    actTorch.setAttribute('aria-pressed', 'false');
  }

  const zoom = camera.zoomRange();
  cellZoom.hidden = zoom === null;
  if (zoom) {
    setZoom.min = String(zoom.min);
    setZoom.max = String(zoom.max);
    setZoom.step = String(zoom.step || 0.1);
  }

  signaling?.sendCameraState(r);
}

setZoom.addEventListener('input', () => void camera.setZoom(Number(setZoom.value)));

/* ---------------------------------------------------------------- peer --- */

async function startPeer(): Promise<void> {
  const stream = camera.mediaStream;
  if (!stream || !signaling?.online) return;

  peer = new Peer({
    state: (state, detail) => {
      fState.textContent =
        state === 'connected'
          ? 'connecté'
          : state === 'negotiating'
            ? 'négociation'
            : state === 'failed'
              ? (detail ?? 'échec')
              : 'inactif';
      fState.dataset.tone = state === 'connected' ? 'ok' : '';
      actTalk.disabled = state !== 'connected' || !pcOnline;
      /* The talk line tracks the peer, so it never tells the user to start
         something that is already running. */
      if (state === 'connected') {
        talkState.textContent = 'Prêt.';
        talkState.dataset.tone = 'idle';
      } else if (state === 'negotiating') {
        talkState.textContent = 'Connexion au PC…';
        talkState.dataset.tone = 'warn';
      } else if (state === 'failed') {
        talkState.textContent = detail ?? 'La connexion a échoué.';
        talkState.dataset.tone = 'bad';
      }
    },
    offer: (sessionId, sdp) => void signaling?.sendOffer(sessionId, sdp),
    ice: (sessionId, candidate) => void signaling?.sendIce(sessionId, candidate),
    transport: showTransport,
    data: onData,
  });

  await peer.start(stream);
  // Send real capabilities with the new sessionId so the PC can gate its controls
  publishCapabilities();
}

function teardownPeer(reason?: string): void {
  if (peer) {
    signaling?.sendBye(peer.session, reason);
    peer.close();
    peer = null;
  }
  actTalk.disabled = true;
  fState.textContent = 'inactif';
  fState.dataset.tone = '';
  fPath.textContent = 'non mesuré';
  fAddr.textContent = 'non mesuré';
  fRtt.textContent = 'non mesuré';
}

/**
 * Hands the new camera track to the existing peer connection.
 *
 * `replaceTrack` keeps the connection, the SSRC and the PC's decoder alive, so
 * flipping the camera or changing resolution is invisible to the PC. Only if
 * there is no sender yet do we negotiate from scratch.
 */
async function publishCameraTrack(): Promise<void> {
  const track = camera.track;
  const stream = camera.mediaStream;
  if (!track || !stream) return;

  if (!peer) {
    if (pcOnline) await startPeer();
    return;
  }
  const swapped = await peer.replaceVideoTrack(track);
  if (!swapped) await peer.start(stream);
}

function showTransport(info: TransportInfo): void {
  /* "Direct LAN" is only claimed when both ends are host candidates on private
     addresses. The phone cannot prove the interface is USB rather than Wi-Fi,
     so it does not claim USB. */
  fPath.textContent = info.directLan
    ? `direct (${info.protocol ?? '—'})`
    : info.localType === 'relay' || info.remoteType === 'relay'
      ? 'relayé'
      : info.localType
        ? `${info.localType} → ${info.remoteType ?? '—'}`
        : 'non mesuré';
  fPath.dataset.tone = info.directLan ? 'ok' : '';
  fAddr.textContent = info.localAddress ?? 'non mesuré';
  fRtt.textContent = info.rttMs !== null ? `${info.rttMs} ms` : 'non mesuré';
}

/* ---------------------------------------------------------------- talk --- */

/* Pointer events rather than touch/mouse pairs: one code path for finger,
   stylus and mouse, and `setPointerCapture` keeps the hold alive if the finger
   slides off the button. */
actTalk.addEventListener('pointerdown', (e) => {
  actTalk.setPointerCapture(e.pointerId);
  void beginTalk();
});
actTalk.addEventListener('pointerup', () => endTalk());
actTalk.addEventListener('pointercancel', () => endTalk());
/* Releasing outside the window still has to end the turn, or the microphone
   would stay open. */
window.addEventListener('blur', () => endTalk());

async function beginTalk(): Promise<void> {
  if (!peer?.connected || talk.active) return;
  try {
    const { turnId, track } = await talk.begin();
    const outcome = await peer.setMicTrack(track);
    if (outcome === 'renegotiate') {
      const re = await peer.renegotiate();
      if (re) signaling?.sendOffer(re.sessionId, re.sdp);
    }
    peer.sendData({ type: 'talk-start', turnId });

    actTalk.setAttribute('aria-pressed', 'true');
    actTalkLabel.textContent = 'Relâchez pour envoyer';
    talkState.textContent = 'Micro ouvert.';
    talkState.dataset.tone = 'ok';
    sayYou.textContent = '…';
  } catch (err) {
    const name = err instanceof DOMException ? err.name : '';
    talkState.textContent =
      name === 'NotAllowedError' ? 'Accès micro refusé.' : 'Le micro ne démarre pas.';
    talkState.dataset.tone = 'bad';
    talk.end();
  }
}

function endTalk(): void {
  if (!talk.active) return;
  const turnId = talk.end();
  void peer?.setMicTrack(null);
  /* "Envoyé" is only claimed when the data channel actually took the frame. */
  const sent = turnId !== null && (peer?.sendData({ type: 'talk-end', turnId }) ?? false);

  actTalk.setAttribute('aria-pressed', 'false');
  actTalkLabel.textContent = 'Maintenir pour parler';
  talkState.textContent = sent ? 'Envoyé.' : 'Le PC n’a pas reçu — lien fermé.';
  talkState.dataset.tone = sent ? 'idle' : 'warn';
}

function onData(msg: DataChannelMessage): void {
  switch (msg.type) {
    case 'transcript':
      captions.hidden = false;
      sayYou.textContent = msg.text || '…';
      break;
    case 'reply':
      captions.hidden = false;
      sayPc.textContent = msg.text || '…';
      break;
    case 'agent-error':
      /* Named subsystem, verbatim message: no collapsing into one banner. */
      talkState.textContent = `${msg.subsystem} : ${msg.message}`;
      talkState.dataset.tone = 'bad';
      break;
    default:
      break;
  }
}

/* -------------------------------------------------------------- settings -- */

function setSheet(open: boolean): void {
  sheet.hidden = !open;
  scrim.hidden = !open;
  barMenu.setAttribute('aria-expanded', String(open));
  if (open) {
    sheet.focus();
  } else {
    barMenu.focus();
  }
}

barMenu.addEventListener('click', () => setSheet(sheet.hidden));
scrim.addEventListener('click', () => setSheet(false));
document.addEventListener('keydown', (e) => {
  if (sheet.hidden) return;
  if (e.key === 'Escape') {
    setSheet(false);
    return;
  }
  /* The sheet is the app's one protected-focus surface: Tab cycles inside it. */
  if (e.key !== 'Tab') return;
  const focusables = sheet.querySelectorAll<HTMLElement>('button:not([disabled]), input, [href]');
  if (focusables.length === 0) return;
  const first = focusables[0]!;
  const last = focusables[focusables.length - 1]!;
  if (e.shiftKey && (document.activeElement === first || document.activeElement === sheet)) {
    last.focus();
    e.preventDefault();
  } else if (!e.shiftKey && document.activeElement === last) {
    first.focus();
    e.preventDefault();
  }
});

sheet.querySelectorAll<HTMLButtonElement>('[data-h]').forEach((b) => {
  b.addEventListener('click', () => {
    const h = Number(b.dataset.h) === 1080 ? 1080 : 720;
    settings = { ...settings, height: h };
    press(b, '[data-h]');
    if (camera.isActive) void restart();
  });
});

sheet.querySelectorAll<HTMLButtonElement>('[data-fps]').forEach((b) => {
  b.addEventListener('click', () => {
    const raw = Number(b.dataset.fps);
    settings = { ...settings, frameRate: raw === 15 ? 15 : raw === 24 ? 24 : 30 };
    press(b, '[data-fps]');
    if (camera.isActive) void restart();
  });
});

function press(chosen: HTMLButtonElement, selector: string): void {
  sheet
    .querySelectorAll<HTMLButtonElement>(selector)
    .forEach((x) => x.setAttribute('aria-pressed', String(x === chosen)));
}

async function restart(): Promise<void> {
  try {
    await camera.start(settings);
    view.srcObject = camera.mediaStream;
    refreshCameraReadout();
    publishCapabilities();
    await publishCameraTrack();
  } catch {
    /* The device refused the new shape; the previous stream is still running,
       so report what is actually live rather than what we asked for. */
    refreshCameraReadout();
  }
}

actUnpair.addEventListener('click', () => forgetPairing('Appairage oublié.'));

function forgetPairing(reason: string): void {
  teardownPeer();
  camera.stop();
  signaling?.stop();
  signaling = null;
  clearCredential();
  credential = null;
  sheet.hidden = true;
  scrim.hidden = true;
  barMenu.setAttribute('aria-expanded', 'false');
  captions.hidden = true;
  screenApp.hidden = true;
  screenPair.hidden = false;
  pairLede.textContent = 'Scannez le QR affiché sur le PC, ou collez le lien d’appairage.';
  setPairState(reason, 'warn');
}

/* ------------------------------------------------------------ lifecycle -- */

document.addEventListener('visibilitychange', () => {
  if (document.hidden) return;
  /* A backgrounded socket is often already dead; probe rather than trust it. */
  signaling?.wake();
});

window.addEventListener('online', () => signaling?.wake());

/* ---------------------------------------------------------------- boot --- */

async function boot(): Promise<void> {
  if ('serviceWorker' in navigator && import.meta.env.PROD) {
    try {
      await navigator.serviceWorker.register('./sw.js');
    } catch {
      /* The app works without it; only offline shell caching is lost. */
    }
  }

  const fromUrl = takePairingFromUrl();
  credential = loadCredential();

  if (fromUrl) {
    /* A QR scan always wins: the user just asked to pair this device, possibly
       replacing a stale credential. */
    pairEndpoint.value = loadEndpoint();
    pairLink.value = `#i=${fromUrl.installId}&t=${fromUrl.token}`;
    pairLede.textContent = 'Lien détecté. Confirmez pour demander l’appairage.';
    setPairState('Prêt à demander.', 'idle');
    await runPairing(fromUrl.installId, fromUrl.token);
    return;
  }

  if (credential) {
    enterApp();
    return;
  }

  if (!window.isSecureContext) {
    setPairState(
      'Cette page doit être servie en HTTPS pour accéder à la caméra.',
      'bad',
    );
  }
}

void boot();
